import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import LoadingText from '../../text/LoadingText';
import RankingRow from './RankingRow';
import RankingCategories from './RankingCategories';
import Img from '../../Img';
import PopupV3 from '../../Popup/PopupV3';
import { PopupTypes } from '../../../utils/constants';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../Analytics/constants';
import { RankingCategoryTypes, LocalizedRankingsInfoText, LocalizedRankingsCtaText, eligibleCountryCodes } from './constants';
import { combineRankings } from './helpers';
import useStartPowerCheckout from '../../hooks/useStartPowerCheckout';

import styles from './localizedRankings.scss';

const CATEGORY_PRIORITY = [
    RankingCategoryTypes.CITY,
    RankingCategoryTypes.STATE,
    RankingCategoryTypes.COUNTRY,
    RankingCategoryTypes.WORLD,
];

const LocalizedRankings = ({ onSegmentClick, onRankClick }) => {
    const history = useHistory();
    const currentProfile = useSelector((state) => state.profile?.data);
    const localizedRankings = useSelector((state) => state.profile?.localizedRankings);
    const isPowerUser = useSelector((state) => state.auth?.user?.isPowered);
    const currentUserPlayerId = useSelector((state) => state.auth?.user?.claimedPlayer?.playerId);
    const currentUserLocation = useSelector((state) => state.auth?.user?.location);
    const { startPowerCheckout, ctaLabel } = useStartPowerCheckout();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showInfoPopup, setShowInfoPopup] = useState(false);

    const currentProfileLocation = currentProfile?.location;
    const { singlesUtrRankings, doublesUtrRankings } = localizedRankings || {};
    const hasSinglesData = singlesUtrRankings?.length > 0;
    const hasDoublesData = doublesUtrRankings?.length > 0;

    const currentProfileId = currentProfile?.id;
    const isCurrentUser = parseInt(currentUserPlayerId) === parseInt(currentProfileId);
    const isCurrentUserCountryEligible = eligibleCountryCodes.includes(currentUserLocation?.countryCode);
    const shouldShowAllCategories = isCurrentUser && isCurrentUserCountryEligible;

    const combinedRankings = useMemo(
        () => combineRankings(singlesUtrRankings, doublesUtrRankings),
        [singlesUtrRankings, doublesUtrRankings],
    );

    const availableCategories = useMemo(() => {
        return CATEGORY_PRIORITY.filter((category) => {
            const categoryKey = category?.toLowerCase();
            return combinedRankings[categoryKey]?.rankings?.length > 0;
        });
    }, [combinedRankings]);

    const categoriesToShow = shouldShowAllCategories ? CATEGORY_PRIORITY : availableCategories;
    const shouldShowCategories = shouldShowAllCategories || availableCategories.length > 1;

    // Manage selectedCategory: initialize and update when needed
    useEffect(() => {
        if (availableCategories?.length === 0) {
            return;
        }

        if (!selectedCategory || (!shouldShowAllCategories && !availableCategories.includes(selectedCategory))) {
            setSelectedCategory(availableCategories[0]);
        }
    }, [availableCategories, selectedCategory, shouldShowAllCategories]);

    const handleOnCategoryClick = (category) => {
        setSelectedCategory(category);
    };

    const handleOnUnlockPowerClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.LOCALIZED_RANKINGS,
        });
    };

    const handleOnInfoButtonClick = () => {
        setShowInfoPopup(true);
    };

    const handleOnInfoPopupClose = () => {
        setShowInfoPopup(false);
    };

    const handleOnEditLocationClick = () => {
        history.push(`/profiles/${currentUserPlayerId}/edit`);
    };

    const gender = currentProfile?.gender?.charAt(0)?.toUpperCase() || '';
    const title = `Verified Tennis Rankings (${gender})`;
    const ctaText = isCurrentUser ? LocalizedRankingsCtaText.SELF : LocalizedRankingsCtaText.OTHER;

    const selectedCategoryData = selectedCategory ? combinedRankings[selectedCategory.toLowerCase()] || null : null;
    const selectedCategoryHasRankingData = selectedCategoryData?.rankings?.length > 0;

    const getCategoryHasLocation = (category, location) => {
        if (!category) {
            return false;
        }
        switch (category) {
            case RankingCategoryTypes.CITY:
                return !!location?.cityName;
            case RankingCategoryTypes.STATE:
                return !!location?.stateName;
            case RankingCategoryTypes.COUNTRY:
                return !!location?.countryName;
            case RankingCategoryTypes.WORLD:
                return true;
            default:
                return true;
        }
    };

    const selectedCategoryHasLocation = getCategoryHasLocation(selectedCategory, currentProfileLocation);
    const shouldShowUpdateLocation = shouldShowAllCategories && !selectedCategoryHasRankingData && !selectedCategoryHasLocation;
    const shouldShowContent = selectedCategory && (selectedCategoryHasRankingData || shouldShowUpdateLocation || shouldShowAllCategories);

    return (
        <div>
            <div className={styles.titleContainer}>
                <h6 className={`${styles.title} text-semi`} data-testid="profile.local-rankings.title">{title}</h6>
                <button
                    type="button"
                    onClick={handleOnInfoButtonClick}
                    className={styles.infoIconButton}
                    data-testid="profile.local-rankings.info-btn"
                >
                    <Img
                        alt="tooltip"
                        src="/img/v4/circle-info-black.svg"
                    />
                </button>
            </div>
            <div className={styles.contentContainer}>
                {localizedRankings?.isLoading ? (
                    <LoadingText text="Loading localized rankings" />
                ) : (
                    shouldShowContent && (
                        <>
                            {!isPowerUser && (
                                <div className={styles.widgetPowerContainerWrapper}>
                                    <div className={styles.widgetPowerContainer}>
                                        <p className={styles.widgetPowerText} data-testid="profile.local-rankings.power-upsell-text">See where you stack up. Upgrade now!</p>
                                        <button type="button" onClick={handleOnUnlockPowerClick} className={styles.powerButton} data-testid="profile.local-rankings.power-upgrade-btn">{ctaLabel('Get Power')}</button>
                                    </div>
                                </div>
                            )}
                            <p className={styles.ctaText} data-testid="profile.local-rankings.cta">
                                <strong data-testid="profile.local-rankings.cta.emphasis">{ctaText.emphasis}</strong>{ctaText.rest}
                            </p>
                            {shouldShowCategories && (
                                <div className={styles.rankingCategoryContainer}>
                                    <RankingCategories
                                        onCategoryClick={handleOnCategoryClick}
                                        selectedCategory={selectedCategory}
                                        availableCategories={availableCategories}
                                        categoriesToShow={categoriesToShow}
                                        shouldShowAllCategories={shouldShowAllCategories}
                                        data-testid="profile.local-rankings.categories"
                                    />
                                </div>
                            )}
                            {shouldShowUpdateLocation ? (
                                <div className={styles.updateLocationContainer}>
                                    <p className={styles.updateLocationText} data-testid="profile.local-rankings.update-location-text">{`Add your ${selectedCategory} in the Residence field of your profile to be eligible for future local rankings.`}</p>
                                    <button type="button" onClick={handleOnEditLocationClick} className={`btn btn-primary btn-small text-uppercase`} data-testid="profile.local-rankings.edit-location-btn">Edit Location</button>
                                </div>
                            ) : selectedCategoryHasRankingData ? (
                                <>
                                    <div className={styles.headerRow}>
                                        <div className={styles.categoryHeader}>
                                            <p className={styles.location} data-testid="profile.local-rankings.location">{selectedCategoryData?.location}</p>
                                        </div>
                                        <div className={styles.rankingsHeader}>
                                            {hasSinglesData && (
                                                <div className={styles.rankingHeaderItem}>
                                                    <Img src="/img/v3/icons/icon-singles-black.svg" alt="Singles" />
                                                </div>
                                            )}
                                            {hasDoublesData && (
                                                <div className={styles.rankingHeaderItem}>
                                                    <Img src="/img/v3/icons/icon-doubles-black.svg" alt="Doubles" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {selectedCategoryData?.rankings?.map((ranking) => (
                                        <RankingRow
                                            key={ranking?.category}
                                            ranking={ranking}
                                            showSingles={hasSinglesData}
                                            onRankClick={onRankClick}
                                            onSegmentClick={onSegmentClick}
                                            showDoubles={hasDoublesData}
                                        />
                                    ))}
                                </>
                            ) : null}
                            <div className={styles.refreshInfo}>
                                <p data-testid="profile.local-rankings.refresh-info">Refreshes weekly on Mondays. Check back for updates.</p>
                            </div>
                        </>
                    )
                )}
                <PopupV3
                    title={LocalizedRankingsInfoText.title}
                    message={LocalizedRankingsInfoText.text}
                    isOpen={showInfoPopup}
                    type={PopupTypes.INFO}
                    onConfirmClick={handleOnInfoPopupClose}
                    confirmButtonLabel="Close"
                    data-testid="profile.local-rankings.info-popup"
                />
            </div>
        </div>
    );
};

LocalizedRankings.propTypes = {
    onSegmentClick: PropTypes.func.isRequired,
    onRankClick: PropTypes.func.isRequired,
};

export default LocalizedRankings;
